/**
 * Generic Google Tag Manager dataLayer utilities
 *
 * This module provides domain-agnostic event tracking through GTM's dataLayer.
 * The application pushes generic domain events (application_started, etc.)
 * and GTM handles routing to specific tracking tools (LinkedIn, GA4, etc.).
 */

import { getUtmData } from "./utmTracking";

// Extend Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

/**
 * Initialize dataLayer if it doesn't exist
 */
function ensureDataLayer(): void {
  window.dataLayer = window.dataLayer || [];
}

/**
 * Push a generic event to the dataLayer
 *
 * @param eventName - The event name (e.g., 'application_started')
 * @param eventData - Additional data to include with the event
 */
export function pushEvent(
  eventName: string,
  eventData: Record<string, any> = {},
): void {
  ensureDataLayer();

  window.dataLayer.push({
    event: eventName,
    ...eventData,
  });
}

/**
 * Push UTM parameters from cookies to the dataLayer
 *
 * Should be called on page load to make UTM data available to all GTM tags.
 * UTM data is also kept in cookies for maximum flexibility.
 */
export function pushUtmData(): void {
  const utmJson = getUtmData();

  if (!utmJson) {
    return;
  }

  try {
    const utmParams = JSON.parse(utmJson);

    ensureDataLayer();
    window.dataLayer.push({
      event: "utm_data_loaded",
      ...utmParams,
    });
  } catch (e) {
    console.warn("Failed to parse UTM data:", e);
  }
}
